import { gql } from '@apollo/client'
import { COMMENTS } from './comments'
import { STREAK_FIELDS } from './streak-fields'
import { ITEM_ADDENDUM_FIELDS } from './item-addendum'

export const ITEM_FIELDS = gql`
  ${STREAK_FIELDS}
  fragment ItemFields on Item {
    id
    parentId
    createdAt
    deletedAt
    title
    url
    excerpt
    editMode
    editExpiresAt
    user {
      id
      name
      meMute
      ...StreakFields
    }
    subs {
      name
      userId
      meMuteSub
      meSubscription
      nsfw
    }
    payIn {
      id
      payInState
      payInType
      payInStateChangedAt
      moneroUri
      payerPrivates {
        payInFailureReason
        retryCount
      }
    }
    moneroWall {
      pricePiconeros
      thresholdPiconeros
      enabledAt
      frozen
      publiclyUnlocked
      locked
      coversComments
      commentsLocked
      myContributionPiconeros
      myRateablePiconeros
      progressPiconeros
      remainingPiconeros
    }
    moneroWallRating {
      average
      count
      myStars
      canRate
      pendingRating
    }
    otsHash
    position
    piconeros
    meAnonPiconeros @client
    boost
    promoBoostPiconeros
    bounty
    bountyPaidTo
    bountyStatus
    bountyPiconeros
    bountyAwardedAt
    noteId
    path
    upvotes
    mePiconeros
    meDontLikePiconeros
    downPiconeros
    commentDownPiconeros
    meBookmark
    meSubscription
    freebie
    netInvestment
    bio
    ncomments
    nDirectComments
    commentPiconeros
    commentCost
    commentBoost
    lastCommentAt
    isJob
    status
    company
    location
    remote
    subNames
    primarySubName
    pollCost
    pollExpiresAt
    uploadId
    mine
    imgproxyUrls
    xPreview
    rel
    apiKey
    cost
    feeStatus
    meCommentsViewedAt
  }`

export const ITEM_FULL_FIELDS = gql`
  ${ITEM_FIELDS}
  ${ITEM_ADDENDUM_FIELDS}
  ${STREAK_FIELDS}
  fragment ItemFullFields on Item {
    ...ItemFields
    ...ItemAddendumFields
    bountyAwardedAt
    bountyWinnerCommentId
    bountyWinnerName
    text
    # walled-discussion replies resolve null text for locked viewers; this
    # flag drives the placeholder in notification/profile surfaces
    commentWalled
    lexicalState
    html
    root {
      id
      createdAt
      title
      bounty
      bountyPaidTo
      bountyStatus
      bountyPiconeros
      subNames
      primarySubName
      mine
      ncomments
      lastCommentAt
      meCommentsViewedAt
      user {
        id
        name
        ...StreakFields
      }
      subs {
        name
        userId
        meMuteSub
        meSubscription
      }
    }
  }`

export const ITEM_OTS_FIELDS = gql`
  fragment ItemOtsFields on Item {
    id
    title
    text
    url
    parentOtsHash
    otsHash
    deletedAt
  }`

export const ITEM_OTS = gql`
  ${ITEM_OTS_FIELDS}

  query Item($id: ID!) {
    item(id: $id) {
      ...ItemOtsFields
    }
  }`

export const POLL_FIELDS = gql`
  fragment PollFields on Item {
    poll {
      meVoted
      count
      options {
        id
        option
        count
      }
      randPollOptions
    }
  }`

export const ITEM = gql`
  ${ITEM_FULL_FIELDS}
  ${POLL_FIELDS}

  query Item($id: ID!) {
    item(id: $id) {
      ...ItemFullFields
      ...PollFields
    }
  }`

export const ITEM_FULL = gql`
  ${ITEM_FULL_FIELDS}
  ${POLL_FIELDS}
  ${COMMENTS}
  query Item($id: ID!, $sort: String, $cursor: String) {
    item(id: $id) {
      ...ItemFullFields
      prior
      ...PollFields
      comments(sort: $sort, cursor: $cursor) {
        cursor
        comments {
          ...CommentsRecursive
        }
      }
    }
  }`

export const RELATED_ITEMS = gql`
  ${ITEM_FIELDS}
  query Related($title: String, $id: ID, $cursor: String, $limit: Limit) {
    related(title: $title, id: $id, cursor: $cursor, limit: $limit) {
      cursor
      items {
        ...ItemFields
      }
    }
  }
`

export const RELATED_ITEMS_WITH_ITEM = gql`
  ${ITEM_FIELDS}
  query Related($title: String, $id: ID!, $cursor: String, $limit: Limit) {
    item(id: $id) {
      ...ItemFields
    }
    related(title: $title, id: $id, cursor: $cursor, limit: $limit) {
      cursor
      items {
        ...ItemFields
      }
    }
  }
`

export const UPDATE_ITEM_USER_VIEW = gql`
  mutation updateCommentsViewAt($id: ID!, $meCommentsViewedAt: Date!) {
    updateCommentsViewAt(id: $id, meCommentsViewedAt: $meCommentsViewedAt)
  }
`

// One-way author removal (bypasses the edit window). Returns the full item so
// the response can be written back into the cache; the server also serves the
// now-public full text on the following refetch.
export const REMOVE_MONERO_WALL = gql`
  ${ITEM_FIELDS}
  mutation removeMoneroWall($id: ID!) {
    removeMoneroWall(id: $id) {
      ...ItemFields
    }
  }
`

// One-way author lift of the discussion wall only (the post body stays
// walled). Bypasses the edit window like removeMoneroWall.
export const OPEN_MONERO_WALL_COMMENTS = gql`
  ${ITEM_FIELDS}
  mutation openMoneroWallComments($id: ID!) {
    openMoneroWallComments(id: $id) {
      ...ItemFields
    }
  }
`
